"use client";

import styled from "styled-components";
import { IoChevronUpSharp } from "react-icons/io5";
import FloatSearchIco from "/public/assets/icons/float-search-ico.png";
import FloatMenuIco from "/public/assets/icons/float-menu-ico.png";
import { useStore } from "@/store";
import { observer } from "mobx-react";
import { RiCloseFill } from "react-icons/ri";
import { SIZES } from "@/components/constants";
import React, { useEffect, useRef, useState } from "react";
import { MODAL_PAYLOAD } from "@/components/constants/modal";
import FloatBtn from "/public/assets/icons/mail-subscribe-ico.png";
import { usePathname } from "next/navigation";

const FloatButton = observer(({ scrollRef, footerObs, className = "" }) => {
    const { floatAreaStore, modalStore } = useStore();
    const [floatActive, setFloatActive] = useState(false);
    const pathname = usePathname();
    const hideLetter = pathname.indexOf("/homepage/news") > -1;
    const wrapRef = useRef(null);
    useEffect(() => {
        if (!scrollRef?.current) return;

        const handleScroll = () => {
            const scrollTop = scrollRef.current.scrollTop;
            setFloatActive(scrollTop > 30);
        };

        const el = scrollRef.current;
        el.addEventListener("scroll", handleScroll);
        return () => el.removeEventListener("scroll", handleScroll);
    }, [scrollRef]);

    const [isFixed, setIsFixed] = useState(false);

    // ✅ “겹치기 직전 stop”을 위한 관측 로직
    useEffect(() => {
        if (!footerObs?.current) return;     // 센서
        if (!scrollRef?.current) return;    // 스크롤 컨테이너
        if (!wrapRef.current) return;       // 버튼 DOM

        const rootEl = scrollRef.current;

        const getSafeGap = () => {
            // 네 stop 스타일이 PC에서 bottom: 15px
            // 모바일은 기본 bottom: 24px라서 화면별로 조금 달리 잡아도 됨
            const isMobile = window.matchMedia(`(max-width: ${SIZES.TABLET})`).matches;
            return isMobile ? 24 : 15;
        };

        const getStopOffset = () => {
            const btnH = wrapRef.current.getBoundingClientRect().height;
            return Math.ceil(btnH + getSafeGap());
        };

        let observer = null;

        const setupObserver = () => {
            const offset = getStopOffset();

            if (observer) observer.disconnect();

            observer = new IntersectionObserver(
                (entries) => {
                    setIsFixed(entries[0]?.isIntersecting === true);
                },
                {
                    // ✅ window가 아니라 HomePageRootWrap(overflow-y:auto)가 root
                    root: rootEl,
                    threshold: 0,
                    // ✅ viewport 하단 경계를 offset만큼 “위로 당겨서”
                    // 센서가 그 경계에 닿기 직전에 stop
                    rootMargin: `0px 0px -${offset}px 0px`,
                }
            );

            observer.observe(footerObs.current);
        };

        setupObserver();

        // 반응형/리사이즈로 버튼 높이가 바뀌면 다시 측정
        const onResize = () => setupObserver();
        window.addEventListener("resize", onResize);

        return () => {
            if (observer) observer.disconnect();
            window.removeEventListener("resize", onResize);
        };
    }, [footerObs, scrollRef]);

    return (
        <ButtonWrap
            ref={wrapRef}
            $floatActive={floatActive}
            className={`${floatAreaStore.isFloatingContent ? "active" : ""} ${isFixed ? "stop" : ""} ${className}`}
        >
            <div className={"fab"}>
                <div
                    className={`btn-inner ${
                        floatAreaStore.isFloatingContent && floatAreaStore.isFloatingContent === "search" ? "close" : ""
                    }`}
                    onClick={() => {
                        if (!floatAreaStore.isFloatingContent) {
                            floatAreaStore.setFloatOpen("search");
                        } else {
                            if (floatAreaStore.isFloatingContent === "menu") {
                                floatAreaStore.setFloatOpen("search");
                            } else {
                                floatAreaStore.setFloatingClear();
                            }
                        }
                    }}
                >
                    <div className={"ico-wrap"}>
                        {floatAreaStore.isFloatingContent && floatAreaStore.isFloatingContent === "search" ? (
                            <RiCloseFill size={24} color={"#fff"} />
                        ) : (
                            <img src={FloatSearchIco?.src} alt={"돋보기 아이콘"} />
                        )}
                    </div>
                </div>

                <div
                    className={`btn-inner ${
                        floatAreaStore.isFloatingContent && floatAreaStore.isFloatingContent === "menu" ? "close" : ""
                    }`}
                    onClick={() => {
                        if (!floatAreaStore.isFloatingContent) {
                            floatAreaStore.setFloatOpen("menu");
                        } else {
                            if (floatAreaStore.isFloatingContent === "search") {
                                floatAreaStore.setFloatOpen("menu");
                            } else {
                                floatAreaStore.setFloatingClear();
                            }
                        }
                    }}
                >
                    <div className={"ico-wrap"}>
                        {floatAreaStore.isFloatingContent && floatAreaStore.isFloatingContent === "menu" ? (
                            <RiCloseFill size={24} color={"#fff"} />
                        ) : (
                            <img src={FloatMenuIco?.src} alt={"플로팅 메뉴 아이콘"} />
                        )}
                    </div>
                </div>
            </div>

            <div
                className={`top-btn ${floatAreaStore.isFloatingContent ? "hidden" : ""}`}
                onClick={() => {
                    scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
                }}
            >
                <IoChevronUpSharp size={18} color={"#000"} />
            </div>

            {hideLetter && (
                <div className={"news-float-area"}>
                    <div
                        className={"img-wrap"}
                        onClick={() => {
                            modalStore.openModal({
                                ...MODAL_PAYLOAD.NEWS_SUBSCRIBE_MODAL("news"),
                                async confirmFunc(closeCallback) {
                                    alert("구독이 완료되었습니다.");
                                    closeCallback();
                                },
                            });
                        }}
                    >
                        <img src={FloatBtn?.src} alt={"구독 버튼"} />
                    </div>
                </div>
            )}
        </ButtonWrap>
    );
});

const ButtonWrap = styled.div`
    position: fixed;
    top: 50%;
    z-index: 102;
    right: 30px;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    flex-direction: column;
    gap: 30px;

    .news-float-area {
        display: none;

        .img-wrap {
            width: 50px;
            cursor: pointer;
        }
    }

    .fab {
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: space-around;
        width: 76px;
        height: 136px;
        background: linear-gradient(166.93deg, rgba(0, 0, 0, 0.04) 0%, rgba(0, 0, 0, 0.16) 100%),
        rgba(255, 255, 255, 0.3);
        border: 1px solid rgba(0, 0, 0, 0.1);
        backdrop-filter: blur(2px);
        border-radius: 99px;

        .btn-inner {
            cursor: pointer;
            width: 56px;
            height: 56px;
            display: flex;
            align-items: center;
            justify-content: center;

            .ico-wrap {
                width: 24px;
                height: 24px;
            }

            &.close {
                background: linear-gradient(180deg, #8064ea 0%, #5435dc 100%);
                border: 1px solid rgba(255, 255, 255, 0.2);
                border-radius: 100%;
            }
        }
    }

    .top-btn {
        width: 76px;
        height: 76px;
        background: #ffffff;
        box-shadow: 1px 1px 2px rgba(0, 0, 0, 0.08), 0px 2px 8px -4px rgba(0, 0, 0, 0.2);
        border-radius: 100%;
        cursor: pointer;
        display: ${({ $floatActive }) => ($floatActive ? "flex" : "none")};
        align-items: center;
        justify-content: center;

        &.hidden {
            opacity: 0;
            visibility: hidden;
        }
    }
    //&.stop {
    //    position: absolute;
    //    bottom: 15px;
    //    top: auto;
    //    transform: translateY(0);
    //}
    //&.page{
    //    &.stop {
    //        bottom: -150px;
    //    }
    //}

    @media (max-width: ${SIZES.TABLET}) {
        top: auto;
        transform: translateY(0);
        right: 20px;
        bottom: 24px;
        gap: 16px;

        &.active {
            display: none;
        }

        .news-float-area {
            display: block;
        }

        .fab {
            width: 52px;
            height: 96px;

            .btn-inner {
                width: 44px;
                height: 44px;

                .ico-wrap {
                }

                &.close {
                }
            }
        }

        .top-btn {
            width: 52px;
            height: 52px;
        }
        
    }
`;

export default FloatButton;
